import { KnowledgeArticle, createArticle } from '../types'

export const documentsArticles: KnowledgeArticle[] = [
  createArticle({
    id: 'document-types',
    title: 'Document Types',
    section: 'documents',
    summary: 'Manage policies, procedures, standards, and guidelines. Documents can be created, uploaded, linked to controls, and generated with AI assistance.',
    content: `
# Document Types

The Documents section helps you manage your organization's security documentation including policies, procedures, and standards.

## Document Categories

### Policies
High-level statements of management intent.
- Information Security Policy
- Acceptable Use Policy
- Data Classification Policy
- Access Control Policy

### Procedures
Step-by-step instructions for performing tasks.
- Incident Response Procedure
- User Access Provisioning Procedure
- Change Management Procedure
- Backup and Recovery Procedure

### Standards
Technical requirements and baselines.
- Password Standard
- Encryption Standard
- Hardening Standard
- Logging Standard

### Guidelines
Recommended practices that are not mandatory.
- Remote Work Guidelines
- BYOD Guidelines
- Secure Development Guidelines

## Managing Documents

### Creating a Document
1. Go to **Documents**
2. Click **New Document**
3. Select document type
4. Enter title and content
5. Link to relevant controls (optional)
6. Save the document

### Uploading Documents
You can also upload existing documents:
- Supported formats: PDF, DOCX, TXT
- Documents are parsed and indexed for search
- Link uploaded documents to controls

## AI Document Generation

Use AI to generate policy templates:

1. Click **Generate with AI**
2. Select the document type
3. Enter a title and optional context
4. Review and edit the generated content
5. Save and link to relevant controls

**Note:** AI-generated documents are starting templates. Always review and customize them for your organization's specific needs.
    `.trim(),
    tags: ['documents', 'policies', 'procedures', 'standards', 'ai-generation'],
    relatedPages: ['/documents'],
    icon: 'FileText',
    order: 1,
  }),

  createArticle({
    id: 'document-lifecycle',
    title: 'Document Lifecycle',
    section: 'documents',
    summary: 'Documents follow a lifecycle: draft, review, approved, published, expired. Track versions, require approvals, and set review schedules.',
    content: `
# Document Lifecycle

Documents in the platform follow a structured lifecycle to ensure proper governance and compliance.

## Document States

### Draft
Initial creation state. Document is being written or edited.
- Can be edited freely
- Not visible to general users
- No compliance credit

### In Review
Document is being reviewed for accuracy and completeness.
- Assigned reviewers can comment
- Author makes revisions
- Requires approval to proceed

### Approved
Document has been reviewed and approved.
- Ready for publication
- Approval recorded with timestamp
- Cannot be edited without new version

### Published
Document is active and in effect.
- Visible to all authorized users
- Provides compliance evidence
- Linked to controls

### Expired
Document has passed its review date.
- Flagged for attention
- May need updates
- Still visible but marked expired

## Version Control

### Creating Versions
When you edit a published document:
1. A new version is created automatically
2. Previous versions are preserved
3. Version history shows all changes

### Version Comparison
Compare versions to see:
- What changed between versions
- Who made the changes
- When changes occurred

## Review Scheduling

### Setting Review Dates
Each document can have a review date:
- **Annual** - Review once per year
- **Semi-annual** - Review every 6 months
- **Quarterly** - Review every 3 months

### Review Notifications
The system notifies document owners:
- 30 days before review due
- On the review date
- When review is overdue

## Best Practices

1. Set realistic review frequencies
2. Assign clear ownership
3. Link documents to relevant controls
4. Keep version notes descriptive
5. Archive rather than delete old documents
    `.trim(),
    tags: ['documents', 'lifecycle', 'version-control', 'review', 'approval'],
    relatedPages: ['/documents'],
    icon: 'History',
    order: 2,
  }),
]
